/* Automation Activity Dashboard — factual Keep-in-Sync analytics only (no ROI estimates).
   Backed by GET /api/crm/oauth/automation-stats; the same measured data can later
   power validated ROI once real customer metrics exist. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Activity, BarChart3 } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const fmt = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString());

const METRICS = [
  ["total_sync_operations", "Total Sync Operations"],
  ["clients_created", "Clients Created"],
  ["matters_created", "Matters/Cases Created"],
  ["documents_synced", "Documents Synced"],
  ["photos_synced", "Photos Synced"],
  ["videos_synced", "Videos Synced"],
  ["drone_media_synced", "Drone Media Synced"],
  ["nurse_docs_synced", "Nurse Documentation Synced"],
  ["timeline_events_synced", "Timeline Events Synced"],
  ["messages_synced", "Messages Synced"],
  ["success_pct", "Successful Sync %"],
  ["failed_syncs", "Failed Syncs"],
  ["retry_recoveries", "Automatic Retry Recoveries"],
];

export const AutomationActivity = ({ dark = false }) => {
  const [stats, setStats] = useState(null);
  const [windowDays, setWindowDays] = useState(30);
  const [scope, setScope] = useState("month");
  useEffect(() => {
    api.get(`/crm/oauth/automation-stats?days=${windowDays}`).then(({ data }) => setStats(data)).catch(() => {});
  }, [windowDays]);
  if (!stats) return null;

  const t = {
    card: dark ? "bg-[#0E1624] border-[#1E2C45]" : "bg-white border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]",
    heading: dark ? "text-white" : "text-navy",
    sub: dark ? "text-slate-400" : "text-slate-500",
    stat: dark ? "bg-[#152033]" : "bg-slate-50",
    chip: (on) => on
      ? (dark ? "bg-blue-600 text-white" : "bg-cobalt text-white")
      : (dark ? "bg-[#152033] text-slate-400 hover:text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"),
  };
  const s = stats[scope];
  const monthTotal = stats.month.total_sync_operations;
  const lastSync = stats.last_successful_sync ? new Date(stats.last_successful_sync).toLocaleString() : "—";
  const accent = dark ? "#3B82F6" : "#1657E2";

  return (
    <div className={`rounded-3xl border p-5 ${t.card}`} data-testid="automation-activity">
      <div className="flex flex-wrap items-center gap-2">
        <BarChart3 className={`w-5 h-5 ${dark ? "text-blue-400" : "text-cobalt"}`} />
        <h3 className={`font-heading font-bold ${t.heading}`}>Automation Activity</h3>
        <span className={`ml-auto text-[11px] ${t.sub}`} data-testid="automation-last-sync">Last successful sync: {lastSync}</span>
      </div>

      <div className={`mt-3 rounded-2xl p-4 flex items-center gap-3 ${t.stat}`} data-testid="automation-summary-card">
        <span className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${dark ? "bg-blue-500/15" : "bg-cobalt-light"}`}>
          <Activity className={`w-5 h-5 ${dark ? "text-blue-400" : "text-cobalt"}`} />
        </span>
        <p className={`text-sm ${t.heading}`}>
          This month your firm processed <b data-testid="automation-month-total">{fmt(monthTotal)}</b> automated CRM synchronization event{monthTotal === 1 ? "" : "s"}.
        </p>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className={`text-[11px] font-semibold uppercase tracking-wide ${t.sub}`}>Totals</span>
        {[["month", "This Month"], ["lifetime", "Lifetime"]].map(([k, l]) => (
          <button key={k} onClick={() => setScope(k)} data-testid={`automation-scope-${k}`}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${t.chip(scope === k)}`}>{l}</button>
        ))}
      </div>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {METRICS.map(([k, label]) => (
          <div key={k} className={`rounded-xl p-3 text-center ${t.stat}`} data-testid={`automation-metric-${k.replace(/_/g, "-")}`}>
            <div className={`font-heading font-extrabold text-lg ${t.heading}`}>{k === "success_pct" && s[k] !== null && s[k] !== undefined ? `${s[k]}%` : fmt(s[k])}</div>
            <div className={`text-[9px] uppercase tracking-wide ${t.sub}`}>{label}</div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <span className={`text-[11px] font-semibold uppercase tracking-wide ${t.sub}`}>Sync trend</span>
        {[[30, "30 days"], [90, "90 days"], [0, "Lifetime"]].map(([k, l]) => (
          <button key={k} onClick={() => setWindowDays(k)} data-testid={`automation-window-${k}`}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${t.chip(windowDays === k)}`}>{l}</button>
        ))}
      </div>
      <div className="mt-2 h-44" data-testid="automation-trend-chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={stats.series} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id="autoGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
                <stop offset="100%" stopColor={accent} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#1E2C45" : "#E2E8F0"} vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 9, fill: dark ? "#64748B" : "#94A3B8" }} tickLine={false} axisLine={false}
              interval="preserveStartEnd" minTickGap={40} />
            <YAxis tick={{ fontSize: 9, fill: dark ? "#64748B" : "#94A3B8" }} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip contentStyle={{ background: dark ? "#0E1624" : "#fff", border: `1px solid ${dark ? "#1E2C45" : "#E2E8F0"}`, borderRadius: 12, fontSize: 12 }} />
            <Area type="monotone" dataKey="count" name="Synced items" stroke={accent} strokeWidth={2} fill="url(#autoGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <p className={`mt-2 text-[10px] ${t.sub}`}>Factual operational analytics measured by the Keep-in-Sync engine — no estimated time or financial savings.</p>
    </div>
  );
};

export default AutomationActivity;
